import { useEffect, useState } from 'react';
import { MapPin, Navigation, Home, Briefcase, Plus, LogOut, Edit, Trash2 } from 'lucide-react';
import { MapAddressSelector } from './MapAddressSelector';
import api from '../../lib/api';

interface SavedAddress {
  id: string;
  type: 'Home' | 'Work' | 'Other';
  label?: string;
  address: string;
  landmark?: string;
  isPrimary?: boolean;
  lat?: number;
  lng?: number;
}

interface AddressDetails {
  formattedAddress: string;
  street?: string;
  area?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  lat: number;
  lng: number;
  landmark?: string;
  houseNumber?: string;
  floor?: string;
  addressType?: 'Home' | 'Work' | 'Other';
  addressLabel?: string;
}

interface LocationSelectionModalProps {
  isOpen: boolean;
  userName?: string;
  onLocationSelect: (location: string) => void;
  onLogout?: () => void;
}

export function LocationSelectionModal({
  isOpen,
  userName = 'User',
  onLocationSelect,
  onLogout,
}: LocationSelectionModalProps) {
  const [showMapSelector, setShowMapSelector] = useState(false);
  const [detectingLocation, setDetectingLocation] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState<string | null>(null);

  const [savedAddresses, setSavedAddresses] = useState<SavedAddress[]>([]);

  const persistUserCoords = (lat?: number, lng?: number) => {
    if (typeof lat !== 'number' || typeof lng !== 'number') return;
    if (Number.isNaN(lat) || Number.isNaN(lng)) return;
    localStorage.setItem('customer_latitude', String(lat));
    localStorage.setItem('customer_longitude', String(lng));
    localStorage.setItem('customer_location_updated_at', String(Date.now()));
  };

  const fetchSavedAddresses = async () => {
    const hasToken = Boolean(localStorage.getItem('auth_token') || localStorage.getItem('token'));
    if (!hasToken) {
      setSavedAddresses([]);
      return;
    }
    try {
      const response = await api.users.getSavedLocations();
      const rows = Array.isArray(response?.data) ? response.data : [];
      const mapped: SavedAddress[] = rows.map((item) => {
        const rawType = String(item.addressType || 'HOME').toUpperCase();
        const type: SavedAddress['type'] =
          rawType === 'OFFICE' ? 'Work' : rawType === 'OTHER' ? 'Other' : 'Home';
        return {
          id: item.id,
          type,
          label: item.label || type,
          address: item.fullAddress,
          landmark: item.deliveryInstructions || undefined,
          isPrimary: Boolean(item.isDefault),
          lat: typeof item.latitude === 'number' ? item.latitude : undefined,
          lng: typeof item.longitude === 'number' ? item.longitude : undefined,
        };
      });
      setSavedAddresses(mapped);
    } catch {
      // Keep UI usable even if API fails.
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchSavedAddresses();
    }
  }, [isOpen]);

  const handleUseCurrentLocation = () => {
    setDetectingLocation(true);

    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      setDetectingLocation(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;

        fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
        )
          .then((response) => response.json())
          .then((data) => {
            const address =
              data.display_name || `Lat: ${latitude.toFixed(4)}, Lon: ${longitude.toFixed(4)}`;
            persistUserCoords(latitude, longitude);
            onLocationSelect(`Current Location: ${address}`);
            setDetectingLocation(false);
          })
          .catch(() => {
            persistUserCoords(latitude, longitude);
            onLocationSelect(`Current Location: ${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
            setDetectingLocation(false);
          });
      },
      (error) => {
        setDetectingLocation(false);

        switch (error.code) {
          case error.PERMISSION_DENIED:
            alert('Location access denied. Please enable location permissions in your browser settings.');
            break;
          case error.POSITION_UNAVAILABLE:
            alert('Location information is unavailable. Please try again.');
            break;
          case error.TIMEOUT:
            alert('Location request timed out. Please try again.');
            break;
          default:
            alert('An unknown error occurred while getting your location.');
            break;
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  };

  const handleSelectAddress = (address: SavedAddress) => {
    persistUserCoords(address.lat, address.lng);
    onLocationSelect(`${address.type}: ${address.address}`);
  };

  const handleEditAddress = (address: SavedAddress) => {
    setEditingAddressId(address.id);
    setShowMapSelector(true);
  };

  const handleDeleteAddress = (addressId: string) => {
    const target = savedAddresses.find((item) => item.id === addressId);
    if (!target) return;

    const confirmed = window.confirm(`Delete ${target.type} address?`);
    if (!confirmed) return;

    let next = savedAddresses.filter((item) => item.id !== addressId);
    if (target.isPrimary && next.length > 0 && !next.some((item) => item.isPrimary)) {
      next = next.map((item, index) => (index === 0 ? { ...item, isPrimary: true } : item));
    }
    setSavedAddresses(next);
  };

  const handleMapAddressSelect = (addressDetails: AddressDetails) => {
    let formattedAddress = '';

    if (addressDetails.houseNumber) {
      formattedAddress += addressDetails.houseNumber;
      if (addressDetails.floor) {
        formattedAddress += `, ${addressDetails.floor}`;
      }
      formattedAddress += ', ';
    }

    formattedAddress += addressDetails.formattedAddress;

    if (addressDetails.landmark) {
      formattedAddress += ` (Near: ${addressDetails.landmark})`;
    }

    if (editingAddressId) {
      setSavedAddresses((prev) =>
        prev.map((item) =>
          item.id === editingAddressId
            ? {
                ...item,
                type: addressDetails.addressType || item.type,
                label: addressDetails.addressLabel || addressDetails.addressType || item.label || item.type,
                address: formattedAddress,
                landmark: addressDetails.landmark || undefined,
                lat: addressDetails.lat,
                lng: addressDetails.lng,
              }
            : item
        )
      );
      setEditingAddressId(null);
    } else {
      persistUserCoords(addressDetails.lat, addressDetails.lng);
      onLocationSelect(`${addressDetails.addressType || 'Other'}: ${formattedAddress}`);
    }
    fetchSavedAddresses();
    setShowMapSelector(false);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Main Location Selection Modal */}
      <div className="fixed inset-0 bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 z-50 flex items-center justify-center p-4 overflow-y-auto">
        <div className="w-full max-w-md">
          {/* Welcome Header */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-3 bg-white px-6 py-3 rounded-full shadow-lg">
              <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-pink-500 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-lg">
                  {userName.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="text-left">
                <p className="text-sm text-gray-600">Welcome,</p>
                <p className="font-bold text-gray-900">{userName}</p>
              </div>
              {onLogout && (
                <button
                  onClick={onLogout}
                  className="ml-2 text-red-500 hover:text-red-600 transition-colors"
                  title="Logout"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>

          {/* Main Card */}
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
            {/* Header Section */}
            <div className="text-center py-8 px-6">
              <div className="w-20 h-20 bg-gradient-to-br from-pink-400 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <MapPin className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-2">
                Select Your Location
              </h2>
              <p className="text-gray-600 text-sm">
                Choose a delivery location to see restaurants near you
              </p>
            </div>

            {/* Content */}
            <div className="px-6 pb-6 space-y-4">
              {/* Use Current Location Button */}
              <button
                onClick={handleUseCurrentLocation}
                disabled={detectingLocation}
                className="w-full bg-gradient-to-r from-orange-400 to-pink-500 text-white py-4 px-6 rounded-2xl font-bold text-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-300 flex items-center justify-center gap-3 group disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                <Navigation
                  className={`w-6 h-6 ${
                    detectingLocation ? 'animate-spin' : 'group-hover:scale-110 transition-transform'
                  }`}
                />
                <span>{detectingLocation ? 'Detecting location...' : 'Use Current Location'}</span>
              </button>

              {/* Divider */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t-2 border-gray-200"></div>
                </div>
                <div className="relative flex justify-center">
                  <span className="px-4 bg-white text-gray-500 font-semibold text-sm">OR</span>
                </div>
              </div>

              {/* Saved Addresses */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <MapPin className="w-5 h-5 text-purple-600" />
                  <h3 className="text-gray-900 font-bold text-lg">Saved Addresses</h3>
                </div>

                <div className="space-y-3">
                  {savedAddresses.map((address) => {
                    const Icon =
                      address.type === 'Home' ? Home : address.type === 'Work' ? Briefcase : MapPin;
                    const iconColor =
                      address.type === 'Home'
                        ? 'text-blue-600'
                        : address.type === 'Work'
                        ? 'text-purple-600'
                        : 'text-pink-600';
                    const iconBg =
                      address.type === 'Home'
                        ? 'bg-blue-100'
                        : address.type === 'Work'
                        ? 'bg-purple-100'
                        : 'bg-pink-100';
                    const hoverBorder =
                      address.type === 'Home'
                        ? 'hover:border-blue-400'
                        : address.type === 'Work'
                        ? 'hover:border-purple-400'
                        : 'hover:border-pink-400';

                    return (
                      <div key={address.id} className="relative">
                        <button
                          type="button"
                          onClick={() => handleSelectAddress(address)}
                          className={`w-full text-left p-4 pr-24 rounded-2xl border-2 border-gray-200 ${hoverBorder} hover:shadow-lg transition-all duration-200 group`}
                        >
                          <div className="flex items-start gap-3">
                            <div
                              className={`w-12 h-12 rounded-xl ${iconBg} flex items-center justify-center flex-shrink-0`}
                            >
                              <Icon className={`w-6 h-6 ${iconColor}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-bold text-gray-900">{address.label || address.type}</h4>
                                {address.isPrimary && (
                                  <span className="text-xs bg-gradient-to-r from-green-400 to-emerald-500 text-white px-2 py-0.5 rounded-full font-semibold">
                                    Primary
                                  </span>
                                )}
                              </div>
                              <p className="text-gray-700 text-sm leading-relaxed font-medium mb-1">
                                {address.address}
                              </p>
                              {address.landmark && (
                                <p className="text-gray-500 text-xs">Near: {address.landmark}</p>
                              )}
                            </div>
                          </div>
                        </button>
                        <div className="absolute top-3 right-3 flex items-center gap-2">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditAddress(address);
                            }}
                            className="w-8 h-8 rounded-full bg-blue-100 hover:bg-blue-200 flex items-center justify-center transition-colors"
                            aria-label={`Edit ${address.type} address`}
                          >
                            <Edit className="w-4 h-4 text-blue-600" />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteAddress(address.id);
                            }}
                            className="w-8 h-8 rounded-full bg-red-100 hover:bg-red-200 flex items-center justify-center transition-colors"
                            aria-label={`Delete ${address.type} address`}
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Add New Address Button */}
              <button
                onClick={() => {
                  setEditingAddressId(null);
                  setShowMapSelector(true);
                }}
                className="w-full py-4 px-6 border-2 border-dashed border-purple-300 rounded-2xl text-purple-600 hover:border-purple-500 hover:bg-purple-50 transition-all duration-200 flex items-center justify-center gap-3 font-bold text-lg group"
              >
                <div className="w-8 h-8 bg-purple-100 group-hover:bg-purple-200 rounded-full flex items-center justify-center transition-colors">
                  <Plus className="w-5 h-5 text-purple-600" />
                </div>
                <span>Add New Address</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Map Address Selector */}
      {showMapSelector && (
        <MapAddressSelector
          isOpen={showMapSelector}
          onClose={() => setShowMapSelector(false)}
          initialLocation={
            editingAddressId
              ? (() => {
                  const addr = savedAddresses.find((item) => item.id === editingAddressId);
                  return addr?.lat !== undefined && addr?.lng !== undefined
                    ? { lat: addr.lat, lng: addr.lng }
                    : undefined;
                })()
              : undefined
          }
          onSelectAddress={handleMapAddressSelect}
          existingLabels={savedAddresses
            .filter((item) => item.id !== editingAddressId)
            .map((item) => item.label || item.type)}
        />
      )}
    </>
  );
}

